import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useRoute, Link } from 'wouter';
import { endpoints } from '@/lib/api';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { StatusBadge, StatusIcon } from '@/components/ui/StatusBadge';
import { Sparkline } from '@/components/ui/Sparkline';
import { ArrowLeft, Play, Square, RotateCw, Power, Pause, PlayCircle } from 'lucide-react';
import { formatBytes, formatPercent, formatUptime } from '@/lib/format';
import { cn } from '@/lib/utils';

type Window = '1h' | '6h' | '24h' | '7d';
const WINDOWS: { value: Window; label: string }[] = [
  { value: '1h', label: '1h' },
  { value: '6h', label: '6h' },
  { value: '24h', label: '24h' },
  { value: '7d', label: '7d' },
];

const REFETCH = 5_000;

export function LxcDetailRoute() {
  const [, params] = useRoute<{ vmid: string }>('/proxmox/lxc/:vmid');
  const vmid = Number.parseInt(params?.vmid ?? '0', 10);
  const [window, setWindow] = useState<Window>('1h');
  const qc = useQueryClient();

  const lxcQ = useQuery({
    queryKey: ['proxmox', 'lxc'],
    queryFn: endpoints.proxmox.lxc,
    refetchInterval: REFETCH,
  });
  const lxc = lxcQ.data?.containers.find((c) => c.vmid === vmid);
  const metricsQ = useQuery({
    queryKey: ['proxmox', 'lxc-metrics', vmid, window],
    queryFn: () => endpoints.proxmox.lxcMetrics(vmid, window),
    refetchInterval: REFETCH,
    enabled: Number.isFinite(vmid) && vmid > 0,
  });

  const control = useMutation({
    mutationFn: (action: 'start' | 'stop' | 'reboot' | 'shutdown' | 'suspend' | 'resume') =>
      endpoints.control.lxc(vmid, action, 'dashboard click'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['proxmox', 'lxc'] });
    },
  });

  if (!Number.isFinite(vmid) || vmid <= 0) {
    return <div className="text-muted-foreground">invalid VMID</div>;
  }

  const points = metricsQ.data?.points ?? [];
  const cpuSeries = points.map((p) => p.cpu);
  const ramSeries = points.map((p) => (p.ram_total > 0 ? (p.ram_used / p.ram_total) * 100 : 0));
  const ramBytes = points.map((p) => p.ram_used);
  const last = points[points.length - 1];

  return (
    <div className="space-y-6">
      <Link href="/proxmox">
        <a className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1">
          <ArrowLeft className="h-3.5 w-3.5" />
          all containers
        </a>
      </Link>

      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <StatusIcon status={lxc?.status ?? 'unknown'} />
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              {lxc?.name ?? `LXC ${vmid}`}
            </h1>
            <p className="text-xs text-muted-foreground font-mono">
              vmid {vmid}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {lxc && <StatusBadge status={lxc.status} />}
          <ActionButton icon={Play} label="Start" onClick={() => control.mutate('start')} disabled={lxc?.status === 'running' || control.isPending} />
          <ActionButton icon={Square} label="Stop" onClick={() => control.mutate('stop')} disabled={lxc?.status === 'stopped' || control.isPending} />
          <ActionButton icon={RotateCw} label="Reboot" onClick={() => control.mutate('reboot')} disabled={lxc?.status !== 'running' || control.isPending} />
          <ActionButton icon={Power} label="Shutdown" onClick={() => control.mutate('shutdown')} disabled={lxc?.status !== 'running' || control.isPending} />
          <ActionButton icon={Pause} label="Suspend" onClick={() => control.mutate('suspend')} disabled={lxc?.status !== 'running' || control.isPending} />
          <ActionButton icon={PlayCircle} label="Resume" onClick={() => control.mutate('resume')} disabled={lxc?.status !== 'paused' || control.isPending} />
        </div>
      </div>

      {control.isError && (
        <div className="rounded-md border border-rose-500/30 bg-rose-500/5 p-3 text-sm text-rose-300">
          Control request failed: {String((control.error as Error)?.message ?? 'unknown')}
        </div>
      )}
      {control.isSuccess && (
        <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3 text-sm text-emerald-300">
          {control.data.action} sent for vmid {control.data.vmid}.
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label="CPU" value={last ? `${last.cpu.toFixed(1)}%` : '—'} tone={!last ? 'default' : last.cpu < 60 ? 'success' : last.cpu < 85 ? 'warn' : 'danger'} />
        <Tile
          label="Memory"
          value={last && last.ram_total > 0 ? formatPercent((last.ram_used / last.ram_total) * 100) : '—'}
          {...(last ? { sub: `${formatBytes(last.ram_used)} / ${formatBytes(last.ram_total)}` } : {})}
        />
        <Tile
          label="Status"
          value={lxc?.status ?? '—'}
          {...(lxc && lxc.status === 'running' ? { sub: formatUptime(lxc.uptime) } : {})}
        />
        <Tile label="Net I/O" value={lxc ? `↓ ${formatBytes(lxc.netIn)} · ↑ ${formatBytes(lxc.netOut)}` : '—'} />
      </div>

      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground">range:</span>
        {WINDOWS.map((w) => (
          <button
            key={w.value}
            onClick={() => setWindow(w.value)}
            className={cn(
              'px-2.5 py-1 rounded-md text-xs font-medium border transition-colors',
              window === w.value
                ? 'bg-accent border-border text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-accent/40',
            )}
          >
            {w.label}
          </button>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>CPU %</CardTitle>
          <span className="text-xs text-muted-foreground tabular-nums">
            {last ? `${last.cpu.toFixed(1)}%` : '—'}
          </span>
        </CardHeader>
        <CardBody className="h-[180px] flex items-center">
          {cpuSeries.length > 1 ? (
            <Sparkline values={cpuSeries} width={1100} height={150} className="w-full" />
          ) : (
            <div className="text-sm text-muted-foreground w-full text-center">collecting samples…</div>
          )}
        </CardBody>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Card>
          <CardHeader>
            <CardTitle>Memory %</CardTitle>
          </CardHeader>
          <CardBody className="h-[150px] flex items-center">
            {ramSeries.length > 1 ? (
              <Sparkline values={ramSeries} width={540} height={120} domain={[0, 100]} className="w-full" />
            ) : (
              <div className="text-sm text-muted-foreground w-full text-center">collecting samples…</div>
            )}
          </CardBody>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Memory used</CardTitle>
          </CardHeader>
          <CardBody className="h-[150px] flex items-center">
            {ramBytes.length > 1 ? (
              <Sparkline values={ramBytes} width={540} height={120} className="w-full" />
            ) : (
              <div className="text-sm text-muted-foreground w-full text-center">collecting samples…</div>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
}

function Tile({
  label,
  value,
  sub,
  tone = 'default',
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: 'default' | 'success' | 'warn' | 'danger';
}) {
  const toneClass = {
    default: 'text-foreground',
    success: 'text-emerald-400',
    warn: 'text-amber-400',
    danger: 'text-rose-400',
  }[tone];
  return (
    <Card>
      <CardBody>
        <div className="text-xs uppercase tracking-wider text-muted-foreground mb-0.5">{label}</div>
        <div className={cn('text-xl font-semibold tabular-nums', toneClass)}>{value}</div>
        {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
      </CardBody>
    </Card>
  );
}

function ActionButton({
  icon: Icon,
  label,
  onClick,
  disabled,
}: {
  icon: typeof Play;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={label}
      className={cn(
        'flex items-center gap-1 px-2 py-1 rounded-md text-xs border transition-colors',
        disabled
          ? 'border-border/30 text-muted-foreground/40 cursor-not-allowed'
          : 'border-border text-foreground hover:bg-accent',
      )}
    >
      <Icon className="h-3 w-3" />
      <span className="hidden md:inline">{label}</span>
    </button>
  );
}
